import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Form,
  Input,
  Button,
  Card,
  DatePicker,
  Select,
  message,
  Space,
  Row,
  Col,
  Divider,
  Spin,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  IdcardOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import dayjs from 'dayjs';

const { Option } = Select;

const DriverEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);

  const fetchDriver = useCallback(async () => {
    try {
      setFetching(true);
      setError(null);
      const response = await apiService.drivers.getById(id);
      const driver = response.data;

      form.setFieldsValue({
        ...driver,
        licenseExpiry: driver.licenseExpiry ? dayjs(driver.licenseExpiry) : null,
      });
    } catch (error) {
      console.error('Error fetching driver:', error);
      setError(error.response?.data?.message || 'Failed to fetch driver details');
      message.error('Failed to fetch driver details');
    } finally {
      setFetching(false);
    }
  }, [id, form]);

  useEffect(() => {
    fetchDriver();
  }, [fetchDriver]);

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const formattedValues = {
        ...values,
        licenseExpiry: values.licenseExpiry?.toISOString(),
      };
      
      await apiService.drivers.update(id, formattedValues);
      message.success('Driver updated successfully');
      navigate('/drivers');
    } catch (error) {
      console.error('Error updating driver:', error);
      message.error(error.response?.data?.message || 'Failed to update driver');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="Edit Driver"
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/drivers')}
          >
            Back to Drivers
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Card title="Personal Information">
                <Form.Item
                  name="name"
                  label="Full Name"
                  rules={[{ required: true, message: 'Please enter driver name' }]}
                >
                  <Input prefix={<UserOutlined />} placeholder="Enter full name" />
                </Form.Item>

                <Form.Item
                  name="phoneNumber"
                  label="Phone Number"
                  rules={[
                    { required: true, message: 'Please enter phone number' },
                    { pattern: /^[0-9]+$/, message: 'Please enter valid phone number' }
                  ]}
                >
                  <Input prefix={<PhoneOutlined />} placeholder="Enter phone number" />
                </Form.Item>

                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { required: true, message: 'Please enter email' },
                    { type: 'email', message: 'Please enter valid email' }
                  ]}
                >
                  <Input prefix={<MailOutlined />} placeholder="Enter email" />
                </Form.Item>
              </Card>
            </Col>

            <Col span={12}>
              <Card title="License Information">
                <Form.Item
                  name="licenseNumber"
                  label="License Number"
                  rules={[{ required: true, message: 'Please enter license number' }]}
                >
                  <Input prefix={<IdcardOutlined />} placeholder="Enter license number" />
                </Form.Item>

                <Form.Item
                  name="licenseExpiry"
                  label="License Expiry Date"
                  rules={[{ required: true, message: 'Please select expiry date' }]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder="Select expiry date"
                    disabledDate={(current) => current && current < dayjs().startOf('day')}
                  />
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Status"
                  rules={[{ required: true, message: 'Please select status' }]}
                >
                  <Select placeholder="Select status">
                    <Option value="available">Available</Option>
                    <Option value="assigned">Assigned</Option>
                    <Option value="unavailable">Unavailable</Option>
                  </Select>
                </Form.Item>
              </Card>
            </Col>
          </Row>

          <Divider />

          <Row justify="end">
            <Space>
              <Button onClick={() => navigate('/drivers')}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                icon={<SaveOutlined />}
              >
                Update Driver
              </Button>
            </Space>
          </Row>
        </Form>
      </Card>
    </div>
  );
};

export default DriverEdit; 